import { useState, useEffect, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { Outlet } from "react-router-dom";
import {
  CalendarIcon,
  HomeIcon,
  UsersIcon,
  ClockIcon,
  Squares2X2Icon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import TopNav from "./TopNav";
import DesktopSidebar from "./DesktopSidebar";
import ModalSidebar from "./ModalSidebar";
import BottomNav from "./BottomNav";
import Container from "./Container";
import ScrollToTop from "../Shared/ScrollToTop";
import { ToastProvider } from "../../contexts/ToastContext";
import ToastContainer from "../Shared/ToastContainer";
import CrispChat from "../Shared/CrispChat";
import PendingEmailBanner from "../Shared/PendingEmailBanner";
import SetupProgressBanner from "../QuickSetup/SetupProgressBanner";

// Context for sharing client data
export const ClientContext = createContext();

export const useClientContext = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error("useClientContext must be used within a ClientProvider");
  }
  return context;
};

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentClient, setCurrentClient] = useState(null);
  const location = useLocation();

  // Clear any --theme-bg-page left on <html> by a prior PublicBookingLayout
  // mount in the same SPA session, so admin <html>/<body> always show cream.
  useEffect(() => {
    document.documentElement.style.removeProperty('--theme-bg-page');
  }, []);

  const navigation = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: HomeIcon,
      current: location.pathname === "/dashboard",
      group: "work",
    },
    {
      name: "Calendar",
      href: "/calendar",
      icon: CalendarIcon,
      current: location.pathname === "/calendar",
      group: "work",
    },
    {
      name: "Clients",
      href: "/clients",
      icon: UsersIcon,
      current:
        location.pathname === "/clients" ||
        location.pathname.includes("/clients/"),
      group: "work",
    },
    {
      name: "Services",
      href: "/services",
      icon: Squares2X2Icon,
      current: location.pathname === "/services",
      group: "setup",
    },
    {
      name: "Availability",
      href: "/availability",
      icon: ClockIcon,
      current: location.pathname === "/availability",
      group: "setup",
    },
    {
      name: "Booking page",
      href: "/booking-page",
      icon: GlobeAltIcon,
      current:
        location.pathname === "/booking-page" ||
        location.pathname.startsWith("/booking-page/"),
      group: "setup",
    },
  ];

  // Get current page information for TopNav
  const getCurrentPageInfo = () => {
    const pathSegments = location.pathname.split("/");

    // Handle client detail pages
    if (location.pathname.startsWith("/clients/") && pathSegments.length > 2) {
      const clientId = pathSegments[2];
      return {
        title: "Clients",
        subtitle: currentClient
          ? currentClient.fullName
          : `Client #${clientId}`,
        isClientDetail: true,
        clientId: clientId,
      };
    }

    // Handle settings pages
    if (location.pathname.startsWith("/settings/") && pathSegments.length > 2) {
      const settingsTab = pathSegments[2];
      const tabName =
        settingsTab.charAt(0).toUpperCase() + settingsTab.slice(1);
      return {
        title: "Settings",
        subtitle: tabName,
        isSettingsTab: true,
      };
    }

    // Settings lives in the profile dropdown (not the sidebar), but still needs a page title
    if (location.pathname === "/settings") {
      return { title: "Settings", subtitle: null };
    }

    // Handle regular pages
    const currentNav = navigation.find((nav) => nav.current);
    return {
      title: currentNav ? currentNav.name : "Page",
      subtitle: null,
    };
  };

  const currentPageInfo = getCurrentPageInfo();

  return (
    <ClientContext.Provider value={{ currentClient, setCurrentClient }}>
      <ToastProvider>
        <div>
          <ScrollToTop />
          <ModalSidebar
            navigation={navigation}
            setSidebarOpen={setSidebarOpen}
            sidebarOpen={sidebarOpen}
          />
          <DesktopSidebar navigation={navigation} />
          <div className="lg:pl-72 bg-cream-50 min-h-screen pb-20 lg:pb-0">
            <TopNav currentPageInfo={currentPageInfo} />
            <PendingEmailBanner />
            <SetupProgressBanner />
            <Container>{<Outlet />}</Container>
          </div>
          <ToastContainer />
          <BottomNav navigation={navigation} />
          <CrispChat />
        </div>
      </ToastProvider>
    </ClientContext.Provider>
  );
};

export default Layout;
